import React, { useState, useEffect } from 'react';
import { ZoomIn, ChevronDown } from 'lucide-react';

interface ImagePanelProps {
  isOpen: boolean;
  imageUrl: string;
  itemDescription: string;
  onZoom: () => void;
  onClose: () => void;
}

export const ImagePanel: React.FC<ImagePanelProps> = ({
  isOpen,
  imageUrl,
  itemDescription,
  onZoom,
  onClose
}) => {
  const [imageFading, setImageFading] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(imageUrl);
  const [previousImageUrl, setPreviousImageUrl] = useState<string | null>(null);

  // Handle image changes with fade effect
  useEffect(() => {
    if (imageUrl !== currentImageUrl && isOpen) {
      setPreviousImageUrl(currentImageUrl);
      setImageFading(true);

      // After fade out, update image and fade in
      const timer = setTimeout(() => {
        setCurrentImageUrl(imageUrl);
        setImageFading(false);
        setPreviousImageUrl(null);
      }, 200); // Half of the transition duration for crossfade effect

      return () => clearTimeout(timer);
    } else if (!previousImageUrl) {
      setCurrentImageUrl(imageUrl);
    }
  }, [imageUrl, isOpen]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setImageFading(false);
      setPreviousImageUrl(null);
    }
  }, [isOpen]);

  return (
    <div
      className={`fixed bottom-0 sm:bottom-16 left-0 right-0 z-40 transition-transform duration-400 ease-in-out ${
        isOpen ? 'translate-y-0' : 'translate-y-full'
      }`}
      style={{ height: '60vh', minHeight: '300px' }}
    >
      <div className="h-full max-w-6xl mx-auto px-2 sm:px-4">
        <div className="h-full grid grid-cols-1 lg:grid-cols-4 gap-0 sm:gap-6">
          {/* Spacer for scanner column - matches the layout */}
          <div className="hidden lg:block" />

          {/* Image panel - matches picking list width (3 columns) */}
          <div className="h-full lg:col-span-3 bg-white border border-gray-200 rounded-t-lg shadow-2xl flex flex-col p-3 sm:p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-3 sm:mb-4 flex-shrink-0">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 truncate mr-2">
                Varebilde - {itemDescription}
              </h3>
              <div className="flex gap-2 sm:gap-3 flex-shrink-0">
                <button
                  onClick={onZoom}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors min-h-[44px]"
                >
                  <ZoomIn size={18} />
                  <span className="font-medium hidden sm:inline">Forstørr</span>
                </button>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  title="Lukk bildevisning"
                >
                  <ChevronDown size={22} />
                </button>
              </div>
            </div>

            {/* Image Container with Crossfade */}
            <div className="flex-1 flex items-center justify-center relative overflow-hidden">
              {/* Previous image (fading out) */}
              {previousImageUrl && (
                <img
                  src={previousImageUrl}
                  alt="Previous"
                  className="absolute inset-0 m-auto max-h-full max-w-full object-contain rounded-lg transition-opacity duration-400"
                  style={{ opacity: imageFading ? 0 : 1 }}
                />
              )}

              {/* Current image (fading in) */}
              <img
                src={currentImageUrl}
                alt={itemDescription}
                className="max-h-full max-w-full object-contain rounded-lg cursor-pointer hover:scale-[1.02] transition-all duration-400 shadow-lg"
                style={{ opacity: imageFading ? 1 : (previousImageUrl ? 0 : 1) }}
                onClick={onZoom}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};